"use client";

import { useCallback, useEffect, useState } from "react";

import { useVisibleAndOnlinePoll } from "@/hooks/use-visible-and-online-poll";
import { Logger } from "@/utils/logger";

// Update story has two halves:
//   1. (here) Tell the browser to recheck `/sw.js` periodically so a new SW
//      installs into the `waiting` slot while the tab is alive.
//   2. (app-update-notification.tsx) Subscribes to the onlineProbeState Zustand
//      store (fed by use-is-online.ts every 10s) and drives the reload countdown
//      when the version changes.
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

export function ServiceWorkerRegistration() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!("serviceWorker" in navigator)) return;

    let cancelled = false;
    const register = () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          if (!cancelled) setRegistration(reg);
        })
        .catch((error: unknown) => {
          Logger.error(error, { message: "Service worker registration failed" });
        });
    };

    // Defer past `load` so SW registration doesn't compete with critical-path
    // resources on low-end devices / slow networks.
    if (document.readyState === "complete") {
      register();
    } else {
      window.addEventListener("load", register, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", register);
    };
  }, []);

  const runUpdate = useCallback(() => {
    if (!registration) return;
    registration.update().catch((error: unknown) => {
      Logger.error(error, { message: "Service worker update check failed" });
    });
  }, [registration]);

  useVisibleAndOnlinePoll(runUpdate, {
    intervalMs: UPDATE_INTERVAL_MS,
    enabled: registration !== null,
  });

  return null;
}
